import React, { useEffect, useState, type ChangeEvent } from "react"
import { useTranslation } from "react-i18next"
import NoResultsFound from "@/components/not-found/no-results-found.tsx"
import PageContentWrapperLarge from "@/components/page-content-wrappers/page-content-wrapper-large.tsx"
import SearchBarGeneral from "@/components/search-bar/search-bar-general.tsx"
import MobileTopBar from "@/components/top-bars/mobile-top-bar/mobile-top-bar.tsx"
import NotesGroup from "@/features/notes/components/notes-group/notes-group.tsx"
import { useSearchNotes } from "@/features/notes/hooks/use-search-notes.ts"
import { NEW_NOTE_VIEW } from "@/features/notes/routes/route-list.ts"
import {
  groupItemsByTimeStampWithField,
  initialItemsTimeGroups,
  type ItemsTimeGroups,
} from "@/utils/helpers/time-grouping.ts"

/**
 * NotesPage component for displaying a list of notes grouped by month and year.
 * Allows searching notes by title or content.
 */
const NotesPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState<string>("")
  const filteredNotes = useSearchNotes(searchQuery)
  const [sortedNotes, setSortedNotes] = useState<ItemsTimeGroups>(
    initialItemsTimeGroups,
  )

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  useEffect(() => {
    setSortedNotes(groupItemsByTimeStampWithField(filteredNotes, "updated_at"))
  }, [filteredNotes])

  const hasNotes = Object.values(sortedNotes).some((list) => list.length)

  return (
    <>
      <MobileTopBar title={t("notes")} navigateTo={NEW_NOTE_VIEW} />
      <PageContentWrapperLarge>
        <SearchBarGeneral
          value={searchQuery}
          onChange={handleSearchInputChange}
          placeholder={t("searchNote")}
          className={"mb-4"}
        />

        {!hasNotes && <NoResultsFound message={t("noNotesFound")} />}
        <NotesGroup notes={sortedNotes} />
      </PageContentWrapperLarge>
    </>
  )
}

export default NotesPage
